import { SortableImageItem } from "./SortableImageItem";
import { PropertyImageResponseDto } from "@/common/api/datasources/Api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  closestCenter,
  closestCorners,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { ImagePlus, Loader2, Trash2, X as CancelIcon, PenSquare as EditIcon, Image } from "lucide-react";
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { restrictToParentElement } from "@dnd-kit/modifiers";

interface PropertyImagesProps {
  images: PropertyImageResponseDto[];
  onUpload: (files: File[]) => void;
  onReorder: (imageOrders: { imageUUID: string; order: number }[]) => void;
  onDelete: (imageUUID: string) => void;
  onDeleteAll: () => void;
  isUploading: boolean;
  isDeleting: boolean;
  isDeletingAll: boolean;
}

export const PropertyImages = ({
  images = [],
  onUpload,
  onReorder,
  onDelete,
  onDeleteAll,
  isUploading,
  isDeleting,
  isDeletingAll,
}: PropertyImagesProps) => {
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [localImages, setLocalImages] =
    useState<PropertyImageResponseDto[]>(images);

  useEffect(() => {
    setLocalImages(images);
  }, [images]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
        delay: 0,
        tolerance: 0,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 100);

    onUpload(files);

    setUploadProgress(100);
    setTimeout(() => setUploadProgress(0), 500);
    event.target.value = "";
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localImages.findIndex(
        (img) => img?.imageUUID === active.id,
      );
      const newIndex = localImages.findIndex(
        (img) => img?.imageUUID === over.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedImages = arrayMove(localImages, oldIndex, newIndex);
        setLocalImages(reorderedImages);

        const reorderedItems = reorderedImages.map((img, index) => ({
          imageUUID: img.imageUUID,
          order: index,
        }));

        onReorder(reorderedItems);
      }
    }
  };

  const validImages = localImages.filter((img) => img?.fullSize?.url);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          <Image className="h-5 w-5 text-primary" />
          {t("properties.sections.images")}
        </CardTitle>
        <div className="flex items-center gap-2">
          {isEditMode && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => document.getElementById("image-upload")?.click()}
                disabled={isUploading}
                className="h-8 w-8 p-0"
              >
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ImagePlus className="h-4 w-4" />
                )}
              </Button>
              {validImages.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteAllDialog(true)}
                  disabled={isDeletingAll}
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  {isDeletingAll ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              )}
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditMode(!isEditMode)}
            className="h-8 w-8 p-0"
          >
            {isEditMode ? (
              <CancelIcon className="h-4 w-4" />
            ) : (
              <EditIcon className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent >
        <input
          id="image-upload"
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />

        <ScrollArea className="h-[400px] rounded-md border">
          {validImages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[400px] p-8 text-center
            hover:bg-gray-100 cursor-pointer
            " onClick={() =>
                document.getElementById("image-upload")?.click()
              }>
              <ImagePlus className="h-12 w-12 text-muted-foreground mb-4" />
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToParentElement]}
            >
              <SortableContext
                items={validImages.map((img) => img.imageUUID)}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-3 p-4">
                  {validImages.map((image, index) => (
                    <SortableImageItem
                      key={image.imageUUID}
                      image={image}
                      isEditing={isEditMode}
                      isDeleting={isDeleting}
                      selectedImage={selectedImage}
                      onDeleteClick={setSelectedImage}
                      isPrimary={index === 0}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </ScrollArea>

        <AlertDialog
          open={!!selectedImage}
          onOpenChange={() => setSelectedImage(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("properties.images.confirmDelete.title")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("properties.images.confirmDelete.description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("properties.images.confirmDelete.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (selectedImage) {
                    onDelete(selectedImage);
                    setSelectedImage(null);
                  }
                }}
              >
                {t("properties.images.confirmDelete.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={showDeleteAllDialog}
          onOpenChange={setShowDeleteAllDialog}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("properties.images.confirmDeleteAll.title")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("properties.images.confirmDeleteAll.description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("properties.images.confirmDeleteAll.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  onDeleteAll();
                  setShowDeleteAllDialog(false);
                }}
              >
                {t("properties.images.confirmDeleteAll.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};
